import { useState, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import api from '@/lib/api'
import { Save, ArrowLeft, CheckCircle, AlertCircle, Plus, FileText, Download, Send, Clock, Lock } from 'lucide-react'
import { formatMyanmarDateTime } from '@/lib/dateUtils'

interface SurveyItem {
  item_no: number
  description: string
  yes_no: string
  qty: string
  remark: string
}

interface SurveyFormData {
  id?: number
  survey_id: number
  form_title: string
  date: string
  project_name: string
  contact_person: string
  contact_phone: string
  address: string
  items: SurveyItem[]
  notes: string
  status?: string
  submitted_by_name?: string | null
  submitted_at?: string | null
}

interface FormSummary {
  id: number
  survey_id: number
  form_title: string
  date: string | null
  project_name: string
  contact_person: string
  status: string
  submitted_by_name: string | null
  submitted_at: string | null
  created_at: string
  updated_at: string
}

interface SiteSurveyInfo {
  id: number
  sr_number: string
  client_name: string
  contact_number: string
  address: string
  project_type: string
}

export default function ClapjSurveyForm({ survey, onClose }: { survey: SiteSurveyInfo; onClose: () => void }) {
  const queryClient = useQueryClient()
  const [activeFormId, setActiveFormId] = useState<number | null>(null)
  const [view, setView] = useState<'list' | 'edit'>('list')
  const [form, setForm] = useState<SurveyFormData>({
    survey_id: survey.id,
    form_title: '',
    date: new Date().toISOString().split('T')[0],
    project_name: survey.client_name || '',
    contact_person: survey.client_name || '',
    contact_phone: survey.contact_number || '',
    address: survey.address || '',
    items: [],
    notes: '',
  })
  const [saved, setSaved] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const [error, setError] = useState('')
  const [downloadingId, setDownloadingId] = useState<number | null>(null)

  const { data: formList = [], isLoading: listLoading } = useQuery<FormSummary[]>({
    queryKey: ['clapj-forms-list', survey.id],
    queryFn: () => api.get(`/clapj-survey/forms/${survey.id}`).then(r => r.data),
  })

  const { data: defaultItems } = useQuery({
    queryKey: ['clapj-survey-items'],
    queryFn: () => api.get('/clapj-survey/items').then(r => r.data),
  })

  const { data: activeFormData, isLoading: formLoading } = useQuery({
    queryKey: ['clapj-form', activeFormId],
    queryFn: () => api.get(`/clapj-survey/form/${activeFormId}`).then(r => r.data),
    enabled: !!activeFormId,
  })

  useEffect(() => {
    if (activeFormData) {
      const items = typeof activeFormData.items === 'string' ? JSON.parse(activeFormData.items) : activeFormData.items
      setForm({
        survey_id: survey.id,
        form_title: activeFormData.form_title || '',
        date: activeFormData.date || '',
        project_name: activeFormData.project_name || '',
        contact_person: activeFormData.contact_person || '',
        contact_phone: activeFormData.contact_phone || '',
        address: activeFormData.address || '',
        items,
        notes: activeFormData.notes || '',
        status: activeFormData.status,
        submitted_by_name: activeFormData.submitted_by_name || null,
        submitted_at: activeFormData.submitted_at || null,
      })
    }
  }, [activeFormData, survey.id])

  const createMutation = useMutation({
    mutationFn: (data: SurveyFormData) => api.post('/clapj-survey', data),
    onSuccess: (res) => {
      const newId = res.data.id
      queryClient.invalidateQueries({ queryKey: ['clapj-forms-list', survey.id] })
      setActiveFormId(newId)
      setSaved(true)
      setError('')
      setTimeout(() => setSaved(false), 3000)
    },
    onError: (err: any) => setError(err.response?.data?.detail || 'Failed to save'),
  })

  const updateMutation = useMutation({
    mutationFn: ({ id, ...data }: Partial<SurveyFormData> & { id: number }) =>
      api.put(`/clapj-survey/form/${id}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['clapj-forms-list', survey.id] })
      queryClient.invalidateQueries({ queryKey: ['clapj-form', activeFormId] })
      setSaved(true)
      setError('')
      setTimeout(() => setSaved(false), 3000)
    },
    onError: (err: any) => setError(err.response?.data?.detail || 'Failed to save'),
  })

  const submitMutation = useMutation({
    mutationFn: (id: number) => api.post(`/clapj-survey/form/${id}/submit`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['clapj-forms-list', survey.id] })
      queryClient.invalidateQueries({ queryKey: ['clapj-form', activeFormId] })
      setSubmitted(true)
      setForm(prev => ({ ...prev, status: 'submitted' }))
      setTimeout(() => setSubmitted(false), 4000)
    },
    onError: (err: any) => setError(err.response?.data?.detail || 'Failed to submit'),
  })

  const handleNewForm = () => {
    const items = defaultItems?.items || []
    const count = formList.length
    setActiveFormId(null)
    setForm({
      survey_id: survey.id,
      form_title: `Survey ${count + 1}`,
      date: new Date().toISOString().split('T')[0],
      project_name: survey.client_name || '',
      contact_person: survey.client_name || '',
      contact_phone: survey.contact_number || '',
      address: survey.address || '',
      items,
      notes: '',
      status: 'draft',
    })
    setError('')
    setSaved(false)
    setView('edit')
  }

  const handleOpenForm = (formId: number) => {
    setActiveFormId(formId)
    setError('')
    setSaved(false)
    setView('edit')
  }

  const handleSave = () => {
    if (activeFormId) {
      updateMutation.mutate({
        id: activeFormId,
        form_title: form.form_title,
        date: form.date,
        project_name: form.project_name,
        contact_person: form.contact_person,
        contact_phone: form.contact_phone,
        address: form.address,
        items: form.items,
        notes: form.notes,
      })
    } else {
      createMutation.mutate(form)
    }
  }

  const handleSubmit = () => {
    if (!activeFormId) {
      setError('Please save the form first before submitting')
      return
    }
    if (!confirm('Submit this form? Once submitted, it cannot be edited.')) return
    submitMutation.mutate(activeFormId)
  }

  const handleDownloadPDF = async (formId: number) => {
    setDownloadingId(formId)
    try {
      const resp = await api.get(`/clapj-survey/form/${formId}/pdf`, { responseType: 'blob' })
      const url = URL.createObjectURL(resp.data)
      const a = document.createElement('a')
      a.href = url
      const contentDisposition = resp.headers['content-disposition'] || ''
      const match = contentDisposition.match(/filename="?([^"]+)"?/)
      a.download = match ? match[1] : `clapj-survey-${formId}.pdf`
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
    } catch {
      setError('Failed to download PDF')
    } finally {
      setDownloadingId(null)
    }
  }

  const updateItem = (index: number, field: keyof SurveyItem, value: string) => {
    const newItems = [...form.items]
    newItems[index] = { ...newItems[index], [field]: value }
    setForm({ ...form, items: newItems })
  }

  const isSaving = createMutation.isPending || updateMutation.isPending
  const isSubmitting = submitMutation.isPending
  const isSubmittedForm = form.status === 'submitted'

  // --- List view ---
  if (view === 'list') {
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between sticky top-0 bg-white z-10 pb-3 border-b border-slate-200">
          <div className="flex items-center gap-3">
            <button onClick={onClose} className="p-1.5 hover:bg-slate-100 rounded-lg">
              <ArrowLeft size={18} className="text-slate-600" />
            </button>
            <div>
              <h2 className="text-lg font-bold text-slate-900">Electric Fence Survey Forms</h2>
              <p className="text-xs text-slate-500">{survey.sr_number} — {survey.client_name}</p>
            </div>
          </div>
          <button onClick={handleNewForm}
            className="flex items-center gap-2 px-4 py-2 bg-red-800 text-white rounded-lg text-sm font-medium hover:bg-red-900">
            <Plus size={14} /> New Form
          </button>
        </div>

        {listLoading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin h-8 w-8 border-4 border-red-800 border-t-transparent rounded-full" />
          </div>
        ) : formList.length === 0 ? (
          <div className="text-center py-16 text-slate-400">
            <FileText size={40} className="mx-auto mb-3 opacity-30" />
            <p className="text-sm font-medium">No survey forms yet</p>
            <p className="text-xs mt-1">Click "New Form" to create the first one</p>
          </div>
        ) : (
          <div className="space-y-3">
            {formList.map(f => (
              <div key={f.id} className="bg-white border border-slate-200 rounded-xl p-4 flex items-center gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold text-slate-900 text-sm">{f.form_title}</span>
                    {f.status === 'submitted' ? (
                      <span className="text-xs px-2 py-0.5 bg-green-100 text-green-700 rounded-full font-medium flex items-center gap-1">
                        <Lock size={10} /> Submitted
                      </span>
                    ) : (
                      <span className="text-xs px-2 py-0.5 bg-amber-100 text-amber-700 rounded-full font-medium flex items-center gap-1">
                        <Clock size={10} /> Draft
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-slate-500 mt-0.5">{f.project_name}{f.date ? ` · ${f.date}` : ''}</p>
                  {f.status === 'submitted' && f.submitted_by_name && (
                    <p className="text-xs text-green-700 mt-1 flex items-center gap-1">
                      <CheckCircle size={10} />
                      Submitted by <span className="font-medium">{f.submitted_by_name}</span>
                      {f.submitted_at ? ` · ${formatMyanmarDateTime(f.submitted_at)}` : ''}
                    </p>
                  )}
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {f.status === 'submitted' && (
                    <button onClick={() => handleDownloadPDF(f.id)}
                      disabled={downloadingId === f.id}
                      className="flex items-center gap-1.5 px-3 py-1.5 text-xs bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200 disabled:opacity-50">
                      <Download size={12} /> {downloadingId === f.id ? 'Downloading...' : 'PDF'}
                    </button>
                  )}
                  <button onClick={() => handleOpenForm(f.id)}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs bg-red-800 text-white rounded-lg hover:bg-red-900">
                    <FileText size={12} /> Open
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    )
  }

  // --- Edit view ---
  if (formLoading && activeFormId) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin h-8 w-8 border-4 border-red-800 border-t-transparent rounded-full" />
      </div>
    )
  }

  return (
    <div className="space-y-3">
      <div className="sticky top-0 bg-white z-10 pb-2 border-b border-slate-200">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 min-w-0">
            <button onClick={() => setView('list')} className="p-1.5 hover:bg-slate-100 rounded-lg shrink-0">
              <ArrowLeft size={18} className="text-slate-600" />
            </button>
            <div className="min-w-0">
              <h2 className="text-base font-bold text-slate-900 truncate">Electric Fence Survey Form</h2>
              <p className="text-xs text-slate-500 truncate">{survey.sr_number} · {form.form_title || 'New Form'}</p>
            </div>
          </div>
          <div className="flex items-center gap-1.5 shrink-0">
            {(saved || submitted) && (
              <span className="text-xs text-green-600 flex items-center gap-0.5 whitespace-nowrap">
                <CheckCircle size={12} /> {submitted ? 'Submitted!' : 'Saved'}
              </span>
            )}
            {isSubmittedForm ? (
              <>
                <span className="text-xs px-1.5 py-0.5 bg-green-100 text-green-700 rounded font-medium flex items-center gap-0.5 whitespace-nowrap">
                  <Lock size={9} /> Submitted
                </span>
                <button onClick={() => activeFormId && handleDownloadPDF(activeFormId)}
                  disabled={downloadingId === activeFormId}
                  className="flex items-center gap-1 px-2.5 py-1.5 bg-slate-700 text-white rounded-lg text-xs font-medium hover:bg-slate-800 disabled:opacity-50 whitespace-nowrap">
                  <Download size={12} /> PDF
                </button>
              </>
            ) : (
              <>
                <button onClick={handleSave} disabled={isSaving}
                  className="flex items-center gap-1 px-2.5 py-1.5 bg-slate-600 text-white rounded-lg text-xs font-medium hover:bg-slate-700 disabled:opacity-50 whitespace-nowrap">
                  <Save size={12} /> {isSaving ? 'Saving…' : 'Save'}
                </button>
                <button onClick={handleSubmit} disabled={isSubmitting || isSaving}
                  className="flex items-center gap-1 px-2.5 py-1.5 bg-red-800 text-white rounded-lg text-xs font-medium hover:bg-red-900 disabled:opacity-50 whitespace-nowrap">
                  <Send size={12} /> {isSubmitting ? 'Submitting…' : 'Submit'}
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      {error && (
        <div className="flex items-center gap-2 bg-red-50 text-red-700 text-sm px-4 py-3 rounded-lg border border-red-200">
          <AlertCircle size={16} className="shrink-0" /><span>{error}</span>
        </div>
      )}

      {isSubmittedForm && form.submitted_by_name && (
        <div className="flex items-center gap-2 bg-green-50 border border-green-200 rounded-lg px-3 py-2">
          <CheckCircle size={14} className="text-green-600 shrink-0" />
          <span className="text-xs text-green-800">
            Submitted by <strong>{form.submitted_by_name}</strong>
            {form.submitted_at ? ` on ${formatMyanmarDateTime(form.submitted_at)}` : ''}
          </span>
        </div>
      )}

      <div className="bg-slate-50 rounded-xl border border-slate-200 p-3">
        <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Form Information</h3>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="text-xs font-medium text-slate-500 block mb-0.5">Form Title</label>
            <input value={form.form_title} onChange={e => setForm({ ...form, form_title: e.target.value })}
              disabled={isSubmittedForm}
              className="w-full px-2.5 py-1.5 text-sm border border-slate-200 rounded-lg bg-white disabled:bg-slate-100 disabled:text-slate-400" />
          </div>
          <div>
            <label className="text-xs font-medium text-slate-500 block mb-0.5">Date</label>
            <input type="date" value={form.date} onChange={e => setForm({ ...form, date: e.target.value })}
              disabled={isSubmittedForm}
              className="w-full px-2.5 py-1.5 text-sm border border-slate-200 rounded-lg bg-white disabled:bg-slate-100 disabled:text-slate-400" />
          </div>
          <div>
            <label className="text-xs font-medium text-slate-500 block mb-0.5">Project Name</label>
            <input value={form.project_name} onChange={e => setForm({ ...form, project_name: e.target.value })}
              disabled={isSubmittedForm}
              className="w-full px-2.5 py-1.5 text-sm border border-slate-200 rounded-lg bg-white disabled:bg-slate-100 disabled:text-slate-400" />
          </div>
          <div>
            <label className="text-xs font-medium text-slate-500 block mb-0.5">Contact Person</label>
            <input value={form.contact_person} onChange={e => setForm({ ...form, contact_person: e.target.value })}
              disabled={isSubmittedForm}
              className="w-full px-2.5 py-1.5 text-sm border border-slate-200 rounded-lg bg-white disabled:bg-slate-100 disabled:text-slate-400" />
          </div>
          <div>
            <label className="text-xs font-medium text-slate-500 block mb-0.5">Contact Phone</label>
            <input value={form.contact_phone} onChange={e => setForm({ ...form, contact_phone: e.target.value })}
              disabled={isSubmittedForm}
              className="w-full px-2.5 py-1.5 text-sm border border-slate-200 rounded-lg bg-white disabled:bg-slate-100 disabled:text-slate-400" />
          </div>
          <div>
            <label className="text-xs font-medium text-slate-500 block mb-0.5">Address</label>
            <input value={form.address} onChange={e => setForm({ ...form, address: e.target.value })}
              disabled={isSubmittedForm}
              className="w-full px-2.5 py-1.5 text-sm border border-slate-200 rounded-lg bg-white disabled:bg-slate-100 disabled:text-slate-400" />
          </div>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="px-4 py-2.5 bg-slate-50 border-b border-slate-200">
          <h3 className="text-sm font-semibold text-slate-700">Survey Checklist</h3>
        </div>
        <div className="overflow-x-auto -webkit-overflow-scrolling-touch">
          <table style={{ minWidth: '560px', width: '100%', borderCollapse: 'collapse' }}>
            <colgroup>
              <col style={{ width: '36px' }} />
              <col style={{ minWidth: '180px' }} />
              <col style={{ width: '76px' }} />
              <col style={{ width: '96px' }} />
              <col style={{ width: '160px' }} />
            </colgroup>
            <thead>
              <tr className="border-b border-slate-200 bg-slate-50">
                <th className="py-2 px-2 text-xs font-semibold text-slate-500 text-center">No</th>
                <th className="py-2 px-2 text-xs font-semibold text-slate-500 text-left">Description</th>
                <th className="py-2 px-1 text-xs font-semibold text-slate-500 text-center">Y / N</th>
                <th className="py-2 px-1 text-xs font-semibold text-slate-500 text-center">Qty</th>
                <th className="py-2 px-2 text-xs font-semibold text-slate-500 text-left">Remark</th>
              </tr>
            </thead>
            <tbody>
              {form.items.map((item, idx) => (
                <tr key={item.item_no} style={{ backgroundColor: idx >= 31 ? '#fffbf0' : idx % 2 === 0 ? '#ffffff' : '#f9fafb' }}
                  className="border-b border-slate-100">
                  <td className="py-1.5 px-2 text-xs text-slate-400 font-medium text-center">{item.item_no}</td>
                  <td className="py-1.5 px-2 text-xs text-slate-700 leading-snug">{item.description}</td>
                  <td className="py-1.5 px-1">
                    <div className="flex items-center justify-center gap-0.5">
                      <button type="button" disabled={isSubmittedForm}
                        onClick={() => updateItem(idx, 'yes_no', item.yes_no === 'Yes' ? '' : 'Yes')}
                        className={`w-7 h-6 text-xs rounded font-semibold transition-colors ${item.yes_no === 'Yes' ? 'bg-green-500 text-white' : 'bg-slate-100 text-slate-400 hover:bg-slate-200'} disabled:cursor-not-allowed`}>
                        Y
                      </button>
                      <button type="button" disabled={isSubmittedForm}
                        onClick={() => updateItem(idx, 'yes_no', item.yes_no === 'No' ? '' : 'No')}
                        className={`w-7 h-6 text-xs rounded font-semibold transition-colors ${item.yes_no === 'No' ? 'bg-red-500 text-white' : 'bg-slate-100 text-slate-400 hover:bg-slate-200'} disabled:cursor-not-allowed`}>
                        N
                      </button>
                    </div>
                  </td>
                  <td className="py-1.5 px-1 text-center">
                    <input
                      type="text"
                      value={item.qty}
                      onChange={e => updateItem(idx, 'qty', e.target.value)}
                      disabled={isSubmittedForm}
                      className="text-sm border border-slate-300 rounded text-center bg-white disabled:bg-slate-100 disabled:text-slate-400 focus:outline-none focus:ring-1 focus:ring-red-400"
                      style={{ width: '88px', height: '28px', padding: '0 6px' }}
                      placeholder="—"
                      maxLength={8}
                    />
                  </td>
                  <td className="py-1.5 px-1.5">
                    <input type="text" value={item.remark} onChange={e => updateItem(idx, 'remark', e.target.value)}
                      disabled={isSubmittedForm}
                      className="w-full text-xs border border-slate-300 rounded bg-white disabled:bg-slate-100 disabled:text-slate-400 focus:outline-none focus:ring-1 focus:ring-red-400"
                      style={{ height: '28px', padding: '0 6px' }}
                      placeholder="—" />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 p-3">
        <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide block mb-1.5">Notes</label>
        <textarea value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })}
          disabled={isSubmittedForm}
          rows={3} className="w-full px-2.5 py-2 text-sm border border-slate-200 rounded-lg disabled:bg-slate-100 disabled:text-slate-400"
          placeholder="Additional observations or notes..." />
      </div>

      {!isSubmittedForm && (
        <div className="flex justify-end gap-2 pb-2">
          <button onClick={handleSave} disabled={isSaving}
            className="flex items-center gap-1.5 px-4 py-2 bg-slate-600 text-white rounded-lg text-sm font-medium hover:bg-slate-700 disabled:opacity-50">
            <Save size={14} /> {isSaving ? 'Saving…' : 'Save Draft'}
          </button>
          <button onClick={handleSubmit} disabled={isSubmitting || isSaving}
            className="flex items-center gap-1.5 px-5 py-2 bg-red-800 text-white rounded-lg text-sm font-medium hover:bg-red-900 disabled:opacity-50">
            <Send size={14} /> {isSubmitting ? 'Submitting…' : 'Submit Form'}
          </button>
        </div>
      )}

      {isSubmittedForm && (
        <div className="flex justify-end pb-2">
          <button onClick={() => activeFormId && handleDownloadPDF(activeFormId)}
            disabled={downloadingId === activeFormId}
            className="flex items-center gap-1.5 px-5 py-2 bg-slate-700 text-white rounded-lg text-sm font-medium hover:bg-slate-800 disabled:opacity-50">
            <Download size={14} /> {downloadingId === activeFormId ? 'Downloading…' : 'Download PDF'}
          </button>
        </div>
      )}
    </div>
  )
}
